import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Stack,
  styled,
  TextField,
  Tooltip,
  tooltipClasses,
  TooltipProps
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import PasswordChecklist from 'react-password-checklist';
import { useNavigate, useParams } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircle';
import { API } from 'src/common/config/API';
import {
  loginBackgroundPath,
  logoWithoutBackground,
  passwordRestSuccessfull,
  paths
} from 'src/common/config/TextMessage';
import { PasswordResetType } from 'src/content/forgot/types/formTypes';
import CommonAlert from 'src/common/CommonAlert';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { commonSx, resetPasswordSx } from 'src/common/CommomSxs';
import EyeIcon from 'src/components/Icons/EyeIcon';
import { signupSxs } from 'src/content/signup/components/signupSx';
// Light Tooltip style
const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: '0px 0px 10px 0 grey',
    fontSize: 11,
    width: '100%'
  }
}));
export default function PasswordReset() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] =
    React.useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<PasswordResetType>();

  const handleTooltipClose = () => {
    setOpen(false);
  };
  const handleTooltipOpen = () => {
    setOpen(true);
  };
  const [openPass, setOpenPass] = React.useState(false);
  const handleTooltipClosePass = () => {
    setOpenPass(false);
  };
  const handleTooltipOpenPass = () => {
    setOpenPass(true);
  };

  // Watch Password
  const watchPassword: string = watch('newPassword');
  const watchPasswordAgain: string = watch('newPassword');
  const watchConfirmPassword: string = watch('confirmPassword');

  // Set Watch password
  useEffect(() => {
    if (watchPassword) {
      setPassword(watchPassword);
    } else {
      setPassword('');
    }
  }, [watchPassword]);
  useEffect(() => {
    if (watchPasswordAgain) {
      setPasswordAgain(watchPasswordAgain);
    } else {
      setPasswordAgain('');
    }
  }, [watchPasswordAgain]);

  // Set Watch confirm password
  useEffect(() => {
    if (watchConfirmPassword) {
      setConfirmPassword(watchConfirmPassword);
    } else {
      setConfirmPassword('');
    }
  }, [watchConfirmPassword]);
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };
  // Submit handler
  const onSubmit: SubmitHandler<PasswordResetType> = async (request) => {
    const { newPassword } = request;
    setLoading(true);
    setError('');

    API.resetPassword(
      API.EndPoint.resetPassword,
      API.Method.post,
      undefined,
      { token: id, password: newPassword },
      (response) => {
        const responsData = { ...response.data };

        if (responsData.status === 'success') {
          setLoading(false);
          setIsSuccessfullySubmitted(true);

          setTimeout(() => {
            navigate(paths?.signin);
          }, 1500);
        } else {
          setError(responsData.message);
          setTimeout(() => {
            setError('');
            setLoading(false);
          }, 2000);
        }
      }
    );
  };

  // Forgot validation validation
  const validationErrorMessage: any = {
    resetPassword: {
      required: 'Password is required',
      pattern: {
        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+-]).{8,}$/gm,
        message: 'Invalid password'
      }
    },
    confirmResetPassword: {
      required: 'Confirm Password is required',
      validate: (val: string) => {
        if (watch('newPassword') != val) {
          return 'Your passwords do no match';
        }
      }
    }
  };
  return (
    <>
      <div
        className="flex justify-center align-middle"
        style={{ width: '100%', height: '100%' }}
      >
        <div className="flex items-center justify-center relative h-full w-full">
          <div
            className="blur-sm login-image-bg "
            style={{ backgroundImage: `url(${loginBackgroundPath})` }}
          />
          <div className="absolute">
            <Card
              className="px-10 py-14 md:px-20 md:pt-16 pb-0 mx-5"
              sx={resetPasswordSx.card}
            >
              <Box>
                <Box className="flex flex-col justify-center items-center ">
                  <Box className="mb-14 flex justify-center">
                    <img
                      src={logoWithoutBackground}
                      loading="lazy"
                      width={'150px'}
                    />
                  </Box>
                  <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                    onSubmit={handleSubmit(onSubmit)}
                    className="mb-16 w-full"
                  >
                    <Stack
                      spacing={2}
                      height="100%"
                      alignItems="center"
                      width="100%"
                    >
                      <ClickAwayListener onClickAway={handleTooltipClose}>
                        <Stack width="100%">
                          <LightTooltip
                            arrow
                            PopperProps={{
                              disablePortal: true
                            }}
                            onClose={handleTooltipClose}
                            open={open}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            placement="bottom"
                            title={
                              <React.Fragment>
                                <PasswordChecklist
                                  iconSize={20}
                                  style={{
                                    ...commonSx?.passwordTooltipTexts,
                                    textAlign: 'left',
                                    zIndex: -1
                                  }}
                                  rules={[
                                    'minLength',
                                    'specialChar',
                                    'number',
                                    'capital',
                                    'lowercase'
                                  ]}
                                  minLength={8}
                                  value={password}
                                  valueAgain={passwordAgain}
                                  onChange={(response) => {}}
                                  messages={{
                                    minLength:
                                      'Must have more than 8 characters',
                                    specialChar:
                                      'Must have atleast one special characters',
                                    number: 'Must have atleast one number',
                                    capital:
                                      'Must have atleast one uppercase characters',
                                    lowercase:
                                      'Must have atleast one lowercase characters'
                                  }}
                                />
                              </React.Fragment>
                            }
                          >
                            <TextField
                              variant="filled"
                              id="outlined-email-input"
                              label="New Password"
                              InputProps={{
                                disableUnderline: true,
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={resetPasswordSx.showPasswordEyeSx}
                                    onClick={handleTogglePasswordVisibility}
                                  >
                                    {showPassword ? (
                                      <VisibilityOffOutlinedIcon
                                        sx={signupSxs?.eyeOffIcon}
                                      />
                                    ) : (
                                      <EyeIcon />
                                    )}
                                  </InputAdornment>
                                )
                              }}
                              type={showPassword ? 'text' : 'password'}
                              size="small"
                              autoComplete="newPassword"
                              color="primary"
                              fullWidth
                              {...register(
                                'newPassword',
                                validationErrorMessage.resetPassword
                              )}
                              helperText={
                                errors.newPassword
                                  ? errors.newPassword.message
                                  : ''
                              }
                              error={!!errors.newPassword}
                              onFocus={handleTooltipOpen}
                              onBlur={handleTooltipClose}
                            />
                          </LightTooltip>
                        </Stack>
                      </ClickAwayListener>

                      <ClickAwayListener onClickAway={handleTooltipClosePass}>
                        <Stack width="100%">
                          <LightTooltip
                            arrow
                            PopperProps={{
                              disablePortal: true
                            }}
                            onClose={handleTooltipClosePass}
                            open={openPass}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            placement="bottom"
                            title={
                              <React.Fragment>
                                <PasswordChecklist
                                  iconSize={20}
                                  style={{
                                    ...commonSx?.passwordTooltipTexts,
                                    textAlign: 'left'
                                  }}
                                  rules={[
                                    'specialChar',
                                    'number',
                                    'capital',
                                    'lowercase',
                                    'match'
                                  ]}
                                  minLength={8}
                                  value={confirmPassword}
                                  valueAgain={passwordAgain}
                                  onChange={(response) => {}}
                                  messages={{
                                    specialChar:
                                      'Must have atleast one special characters',
                                    number: 'Must have atleast one number',
                                    capital:
                                      'Must have atleast one uppercase characters',
                                    lowercase:
                                      'Must have atleast one lowercase characters',
                                    match: 'Both passwords must match'
                                  }}
                                />
                              </React.Fragment>
                            }
                          >
                            <TextField
                              variant="filled"
                              id="outlined-email-input_1"
                              label="Confirm Password"
                              type={showNewPassword ? 'text' : 'password'}
                              autoComplete="confirmPassword"
                              InputProps={{
                                disableUnderline: true,
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={resetPasswordSx.showPasswordEyeSx}
                                    onClick={handleToggleNewPasswordVisibility}
                                  >
                                    {showNewPassword ? (
                                      <VisibilityOffOutlinedIcon
                                        sx={signupSxs?.eyeOffIcon}
                                      />
                                    ) : (
                                      <EyeIcon />
                                    )}
                                  </InputAdornment>
                                )
                              }}
                              size="small"
                              fullWidth
                              {...register(
                                'confirmPassword',
                                validationErrorMessage.confirmResetPassword
                              )}
                              helperText={
                                errors.confirmPassword
                                  ? errors.confirmPassword.message
                                  : ''
                              }
                              error={!!errors.confirmPassword}
                              onFocus={handleTooltipOpenPass}
                              onBlur={handleTooltipClosePass}
                            />
                          </LightTooltip>
                        </Stack>
                      </ClickAwayListener>

                      <Stack spacing={2} width="100%">
                        <Button
                          fullWidth
                          variant="contained"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? (
                            <CircularProgress size={24} />
                          ) : (
                            'Reset Password'
                          )}
                        </Button>
                      </Stack>
                    </Stack>
                    <div className="mt-2">
                      {isSuccessfullySubmitted && (
                        <Alert
                          icon={
                            <CheckCircleOutlineIcon
                              fontSize="inherit"
                              sx={resetPasswordSx.successMessage}
                            />
                          }
                          severity="success"
                        >
                          {passwordRestSuccessfull}
                        </Alert>
                      )}
                      {error && (
                        <CommonAlert severity="error" message={error} />
                      )}
                    </div>
                  </Box>
                </Box>
              </Box>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
